/**
 * React hooks for fetching models and providers from API
 */

import { useState, useEffect } from 'react';
import { fetchModels, fetchProviders, type ModelsResponse, type ProvidersResponse } from '../lib/api/models';

interface UseModelsResult {
  models: ModelsResponse['models'];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseProvidersResult {
  providers: ProvidersResponse['providers'];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to fetch all models with provider info
 */
export function useModels(): UseModelsResult {
  const [models, setModels] = useState<ModelsResponse['models']>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchModels();
      setModels(data.models);

      console.log('[useModels] Fetch complete:', {
        modelsCount: data.models.length,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch models';
      setError(errorMsg);
      console.error('[useModels] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { models, loading, error, refetch: fetchData };
}

/**
 * Hook to fetch all providers
 */
export function useProviders(): UseProvidersResult {
  const [providers, setProviders] = useState<ProvidersResponse['providers']>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchProviders();
      setProviders(data.providers);

      console.log('[useProviders] Fetch complete:', {
        providersCount: data.providers.length,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch providers';
      setError(errorMsg);
      console.error('[useProviders] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { providers, loading, error, refetch: fetchData };
}
